import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cutlery, Truck, Camera, Bag, NavArrowDown, Calendar, Community, ChatBubble, Link as LinkIcon, GridPlus, List } from 'iconoir-react';
import { getLojaInfo, verifyToken, getHomeLinks } from '../services/api';
import SEOMetaTags from '../components/SEOMetaTags';
import Footer from '../components/Footer';
import LoginModal from '../components/LoginModal';
import MeusPedidosOffcanvas from '../components/MeusPedidosOffcanvas';
import { getStatusLoja, formatarReabertura, NOMES_DIAS } from '../utils/horarioUtils';
import '../components/Loja.css';

const Loja = ({ brand }) => {
  const [loja, setLoja] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lojaStatus, setLojaStatus] = useState({ aberta: false, status: 'FORA_DO_HORARIO', mensagem: '', cor: 'danger', badge: 'FECHADO' });
  const [showHorarios, setShowHorarios] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [backgroundColor, setBackgroundColor] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showMeusPedidos, setShowMeusPedidos] = useState(false);
  const [usuario, setUsuario] = useState(null);
  const [customLinks, setCustomLinks] = useState([]);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('loja_view_mode') || 'grid');

  // Salvar preferência no localStorage
  const toggleViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem('loja_view_mode', mode);
  };

  /**
   * Extrai a cor dominante de uma imagem (logo) usando canvas.
   * Ignora pixels transparentes e brancos puros.
   */
  const extrairCorDominante = (imageUrl) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const size = 50; // Reduz para performance
          canvas.width = size;
          canvas.height = size;
          ctx.drawImage(img, 0, 0, size, size);
          const imageData = ctx.getImageData(0, 0, size, size).data;

          let r = 0, g = 0, b = 0, count = 0;

          for (let i = 0; i < imageData.length; i += 4) {
            const pr = imageData[i];
            const pg = imageData[i + 1];
            const pb = imageData[i + 2];
            const alpha = imageData[i + 3];

            // Ignorar pixels transparentes e brancos/quase brancos
            if (alpha < 128) continue;
            if (pr > 240 && pg > 240 && pb > 240) continue;

            r += pr;
            g += pg;
            b += pb;
            count++;
          }

          if (count > 0) {
            r = Math.round(r / count);
            g = Math.round(g / count);
            b = Math.round(b / count);
            resolve(`rgb(${r}, ${g}, ${b})`);
          } else {
            resolve('#1a1a1a');
          }
        } catch (e) {
          resolve('#1a1a1a');
        }
      };
      img.onerror = () => resolve('#1a1a1a');
      img.src = imageUrl;
    });
  };

  useEffect(() => {
    fetchLojaData(true); // Primeira carga - define imagem de fundo
    
    // Polling para atualizar estado a cada 15 segundos (sem trocar imagem)
    const pollInterval = setInterval(() => {
      fetchLojaData(false);
    }, 15000);
    
    return () => clearInterval(pollInterval);
  }, [brand]);

  // Atualizar status quando loja mudar
  useEffect(() => {
    if (loja) {
      setLojaStatus(getStatusLoja(loja));
    }
  }, [loja]);

  // Verificar status a cada 30 segundos (para mudanças de horário)
  useEffect(() => {
    if (!loja) return;
    
    const interval = setInterval(() => {
      setLojaStatus(getStatusLoja(loja));
    }, 30000);
    
    return () => clearInterval(interval);
  }, [loja]);

  useEffect(() => {
    const verificarToken = async () => {
      const token = localStorage.getItem('sitri_token');
      if (token) {
        try {
          const resultado = await verifyToken(token);
          if (resultado && resultado.success && resultado.usuario) {
            setUsuario(resultado.usuario);
          }
        } catch (error) {}
      } else {
        const usuarioSalvo = localStorage.getItem('sitri_user');
        if (usuarioSalvo) {
          try { setUsuario(JSON.parse(usuarioSalvo)); } catch (error) {}
        }
      }
    };
    verificarToken();
  }, []);

  const fetchLojaData = async (isInitialLoad = false) => {
    try {
      const response = await getLojaInfo(brand);
      const lojaData = response.loja;
      setLoja(lojaData);

      // Buscar links personalizados da home
      if (isInitialLoad) {
        const homeLinksData = await getHomeLinks(brand);
        // Formatar links para exibição
        const linksFormatados = (homeLinksData.links || []).map(link => ({
          href: link.url,
          label: link.titulo,
          icon: link.imagem ? (
            <img src={`https://grubupload.s3.amazonaws.com/${link.imagem.imagem}`} alt={link.titulo} width={32} height={32} style={{ borderRadius: '6px', objectFit: 'cover' }} />
          ) : (
            <div style={{ width: 32, height: 32, borderRadius: '6px', backgroundColor: link.cor_fundo || '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <LinkIcon width={18} height={18} color={link.cor_texto || '#fff'} />
            </div>
          ),
          destaque: link.b_destaque
        }));
        setCustomLinks(linksFormatados);
      }

      // Só define a imagem de fundo na primeira carga (não no polling)
      if (isInitialLoad) {
        if (lojaData.header_fotos && Array.isArray(lojaData.header_fotos) && lojaData.header_fotos.length > 0) {
          // Usar fotos selecionadas pelo dono da loja para o header
          const randomIndex = Math.floor(Math.random() * lojaData.header_fotos.length);
          setBackgroundImage(`url(https://grubupload.s3.amazonaws.com/${lojaData.header_fotos[randomIndex].url})`);
        } else if (lojaData.logo_url) {
          // Fallback: extrair cor dominante do logo e usar como fundo sólido
          const logoUrl = `https://grubupload.s3.amazonaws.com/${lojaData.logo_url}`;
          const corDominante = await extrairCorDominante(logoUrl);
          setBackgroundColor(corDominante);
          setBackgroundImage('none');
        } else {
          setBackgroundColor('#1a1a1a');
          setBackgroundImage('none');
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (userData) => {
    setUsuario(userData);
    localStorage.setItem('sitri_user', JSON.stringify(userData));
    setShowLogin(false);
  };

  const handleLocationClick = () => {
    if (loja?.endereco) {
      const { logradouro, numero, cidade } = loja.endereco;
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${logradouro}, ${cidade}, ${numero}&travelmode=driving`, '_blank');
    }
  };

  // Agrupar horários por dia e combinar múltiplos turnos
  // IMPORTANTE: Este useMemo deve estar ANTES dos early returns para manter a ordem dos hooks
  const horariosAgrupados = React.useMemo(() => {
    if (!loja) return [];
    
    // Ordem dos dias: Domingo(1), Segunda(2), Terça(4), Quarta(8), Quinta(16), Sexta(32), Sábado(64)
    const ordemDias = [1, 2, 4, 8, 16, 32, 64];
    
    // Coletar todos os turnos por dia
    const turnosPorDia = {};
    
    // Inicializar todos os dias
    ordemDias.forEach(dia => {
      turnosPorDia[dia] = [];
    });
    
    // Adicionar horários (cada registro é um turno)
    if (loja.horarios && loja.horarios.length > 0) {
      loja.horarios.forEach(h => {
        if ((h.ativo === true || h.ativo === 1) && turnosPorDia[h.dia] !== undefined) {
          // Evitar duplicatas
          const jaExiste = turnosPorDia[h.dia].some(turno => 
            turno.horario_inicial === h.horario_inicial && 
            turno.horario_final === h.horario_final
          );
          if (!jaExiste) {
            turnosPorDia[h.dia].push({
              horario_inicial: h.horario_inicial,
              horario_final: h.horario_final
            });
          }
        }
      });
    }
    
    // Converter para array ordenado
    return ordemDias.map(dia => ({
      dia,
      turnos: turnosPorDia[dia].sort((a, b) => {
        // Ordenar por horário inicial
        const aMin = parseInt(a.horario_inicial?.substring(0, 2) || '0') * 60 + parseInt(a.horario_inicial?.substring(3, 5) || '0');
        const bMin = parseInt(b.horario_inicial?.substring(0, 2) || '0') * 60 + parseInt(b.horario_inicial?.substring(3, 5) || '0');
        return aMin - bMin;
      }),
      ativo: turnosPorDia[dia].length > 0
    }));
  }, [loja]);

  // Formatar turnos para exibição
  const formatarTurnosDia = (turnos) => {
    if (!turnos || turnos.length === 0) return 'Fechado';
    return turnos.map(t => 
      `${t.horario_inicial?.substring(0, 5) || '00:00'} - ${t.horario_final?.substring(0, 5) || '00:00'}`
    ).join(' e ');
  };

  if (loading) {
    return (
      <div className="loja-page">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!loja) {
    return (
      <div className="loja-page">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
          <h6 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a1a1a' }}>Loja não encontrada</h6>
        </div>
      </div>
    );
  }

  // Links principais (sempre habilitados primeiro, depois os que dependem da loja estar aberta)
  const mainLinks = [];
  mainLinks.push({ to: '/cardapio', icon: <Cutlery width={24} height={24} />, label: 'Ver Cardápio' });
  if (loja.configuracoes?.b_reservas === 1) mainLinks.push({ to: '/reserva', icon: <Calendar width={24} height={24} />, label: 'Reservar Mesa' });
  if (loja.configuracoes?.b_delivery === 1) mainLinks.push({ to: '/delivery', icon: <Truck width={24} height={24} />, label: 'Pedir Delivery', badge: 'DESCONTOS' });
  if (loja.configuracoes?.b_balcao === 1) mainLinks.push({ to: '/balcao', icon: <Bag width={24} height={24} />, label: 'Pedir no Balcão' });
  if (loja.configuracoes?.b_fila === 1) mainLinks.push({ to: '/fila', icon: <Community width={24} height={24} />, label: 'Fila de Atendimento' });
  if (loja.configuracoes?.b_feedback === 1) mainLinks.push({ to: '/feedback', icon: <ChatBubble width={24} height={24} />, label: 'Deixar uma Avaliação' });

  // Variáveis derivadas do status
  const isFechamentoTemporario = lojaStatus.status === 'FECHAMENTO_TEMPORARIO';
  const isOpen = lojaStatus.aberta;
  const fechamentoData = loja?.fechamento_temporario;

  // Verificar se link deve estar desabilitado (quando fechada, exceto Ver Cardápio e Reservar Mesa)
  const isLinkDisabled = (link) => {
    // Ver Cardápio sempre fica habilitado - não afeta operações da loja
    if (link.to === '/cardapio') return false;
    // Reservar Mesa sempre fica habilitado - cliente pode reservar para outro dia
    if (link.to === '/reserva') return false;
    // Avaliação sempre fica habilitada - cliente pode avaliar a qualquer momento
    if (link.to === '/feedback') return false;
    return !lojaStatus.aberta;
  };

  // Ordenar links: habilitados primeiro, desabilitados depois
  const sortedMainLinks = [...mainLinks].sort((a, b) => {
    const aDisabled = isLinkDisabled(a) ? 1 : 0;
    const bDisabled = isLinkDisabled(b) ? 1 : 0;
    return aDisabled - bDisabled;
  });

  // Links externos (redes sociais)
  const socialLinks = [];
  if (loja.instagram) socialLinks.push({ href: loja.instagram, icon: <img src="https://grubicons.s3.us-east-1.amazonaws.com/logo-instagram.png" alt="Instagram" width={32} height={32} />, label: 'Instagram' });
  if (loja.twitter) socialLinks.push({ href: loja.twitter, icon: <img src="https://grubicons.s3.us-east-1.amazonaws.com/IFood_logo.svg" alt="iFood" width={24} height={24} />, label: 'iFood' });
  if (loja.whatsapp) socialLinks.push({ href: `https://api.whatsapp.com/send?phone=55${loja.whatsapp}`, icon: <img src="https://grubicons.s3.us-east-1.amazonaws.com/logo-whatsapp.png" alt="WhatsApp" width={32} height={32} />, label: 'WhatsApp' });
  if (loja.endereco) socialLinks.push({ onClick: handleLocationClick, icon: <img src="https://grubicons.s3.us-east-1.amazonaws.com/logo-google-maps.png" alt="Como Chegar" width={32} height={32} />, label: 'Como Chegar' });

  const horariosList = horariosAgrupados.length > 0 ? (
    <div className="loja-horarios-lista-content">
      {horariosAgrupados.map((horario, index) => {
        return (
          <div key={index} className="d-flex justify-content-between align-items-center" style={{ fontSize: '0.85rem' }}>
            <span>{NOMES_DIAS[horario.dia]}</span>
            <span style={{ fontWeight: 600 }}>
              {horario.ativo ? formatarTurnosDia(horario.turnos) : 'Fechado'}
            </span>
          </div>
        );
      })}
    </div>
  ) : null;

  return (
    <>
      <SEOMetaTags loja={loja} brand={brand} />
      <div className={`loja-page${isFechamentoTemporario ? ' loja-page--fechada' : ''}`}>

        {/* ===== DESKTOP LAYOUT ===== */}
        <div className="loja-desktop">
          <div className="loja-cover" style={{
            backgroundColor: backgroundColor || '#000'
          }}>
            {/* Imagem de fundo - com grayscale se fechada */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: backgroundImage || 'none',
              backgroundColor: backgroundColor || 'transparent',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center center',
              backgroundSize: 'cover',
              filter: isFechamentoTemporario ? 'grayscale(1)' : ((loja.header_fotos && loja.header_fotos.length > 0) || (loja.galeria && loja.galeria.length > 0) ? 'none' : 'none'),
              zIndex: 0
            }} />
            <div className="loja-cover-overlay" />
            <div className="loja-cover-content">
              {loja.logo_url && (
                <img
                  src={`https://grubupload.s3.amazonaws.com/${loja.logo_url}`}
                  alt={loja.nome}
                  className="loja-cover-logo"
                />
              )}
              <h1 className="loja-cover-nome">{loja.nome}</h1>
              {loja.tipo && (
                <span className="loja-cover-tipo">{loja.tipo}</span>
              )}
              {loja.horarios && loja.horarios.length > 0 && (
                <div className="loja-cover-status">
                  {isFechamentoTemporario ? (
                    <span className="badge bg-warning text-dark rounded-pill status-badge">{lojaStatus.badge}</span>
                  ) : isOpen ? (
                    <span className="badge bg-success rounded-pill status-badge">{lojaStatus.badge}</span>
                  ) : (
                    <span className="badge bg-danger rounded-pill status-badge">{lojaStatus.badge}</span>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '8px', marginTop: '8px' }}>
                    <button
                      className="loja-cover-horarios-btn"
                      onClick={() => setShowHorarios(!showHorarios)}
                      style={{ margin: 0 }}
                    >
                      Horários de funcionamento
                      <NavArrowDown
                        width={14}
                        height={14}
                        style={{ transform: showHorarios ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
                      />
                    </button>
                    {isFechamentoTemporario && (fechamentoData?.motivo || fechamentoData?.reabertura_programada) && (
                      <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: '8px', padding: '8px 14px', display: 'inline-block' }}>
                        {fechamentoData?.motivo && (
                          <span className="text-white d-block" style={{ fontSize: '0.85rem' }}>
                            {fechamentoData.motivo}
                          </span>
                        )}
                        {fechamentoData?.reabertura_programada && (
                          <span className="text-white d-block" style={{ fontSize: '0.8rem', opacity: 0.85, marginTop: fechamentoData?.motivo ? '4px' : '0' }}>
                            Reabertura prevista: {formatarReabertura(fechamentoData.reabertura_programada)}
                          </span>
                        )}
                      </div>
                    )}
                    {!isOpen && !isFechamentoTemporario && lojaStatus.proximaAbertura && (
                      <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: '8px', padding: '8px 14px', display: 'inline-block' }}>
                        <span className="text-white d-block" style={{ fontSize: '0.8rem', opacity: 0.85 }}>
                          Próxima abertura: {lojaStatus.proximaAbertura}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {showHorarios && (
                <div className="loja-cover-horarios-lista">
                  {horariosList}
                </div>
              )}
            </div>
          </div>

          <div className="loja-body">
            <div className="loja-section">
              <div className="loja-section-header">
                <h5 className="loja-section-title">Como podemos te atender hoje?</h5>
                <div className="loja-view-toggle">
                  <button 
                    className={`loja-view-btn${viewMode === 'grid' ? ' active' : ''}`}
                    onClick={() => toggleViewMode('grid')}
                    title="Visualizar em grade"
                  >
                    <GridPlus width={18} height={18} />
                  </button>
                  <button 
                    className={`loja-view-btn${viewMode === 'list' ? ' active' : ''}`}
                    onClick={() => toggleViewMode('list')}
                    title="Visualizar em lista"
                  >
                    <List width={18} height={18} />
                  </button>
                </div>
              </div>
              <div className={viewMode === 'list' ? 'loja-links-list' : 'loja-links-grid'}>
                {sortedMainLinks.map((link, i) => (
                  isLinkDisabled(link) ? (
                    <div key={i} className={`loja-grid-card loja-grid-card--disabled${viewMode === 'list' ? ' loja-list-card' : ''}`} title="Loja temporariamente fechada">
                      <div className="loja-grid-icon">{link.icon}</div>
                      <span className="loja-grid-label">{link.label}</span>
                    </div>
                  ) : (
                    <Link key={i} to={link.to} className={`loja-grid-card${link.badge ? ' loja-grid-card--has-badge' : ''}${viewMode === 'list' ? ' loja-list-card' : ''}`}>
                      {link.badge && (
                        <div className="loja-ribbon">
                          {link.badge}
                          <div className="loja-ribbon-shine" />
                        </div>
                      )}
                      <div className="loja-grid-icon">{link.icon}</div>
                      <span className="loja-grid-label">{link.label}</span>
                    </Link>
                  )
                ))}
              </div>
            </div>

            {socialLinks.length > 0 && (
              <div className="loja-section">
                <h5 className="loja-section-title">Nossos canais e contatos:</h5>
                <div className={viewMode === 'list' ? 'loja-links-list' : 'loja-links-grid'}>
                  {socialLinks.map((link, i) => (
                    link.onClick ? (
                      <button key={i} className={`loja-grid-card${viewMode === 'list' ? ' loja-list-card' : ''}`} onClick={link.onClick}>
                        <div className="loja-grid-icon">{link.icon}</div>
                        <span className="loja-grid-label">{link.label}</span>
                      </button>
                    ) : (
                      <a key={i} href={link.href} target="_blank" rel="noopener noreferrer" className={`loja-grid-card${viewMode === 'list' ? ' loja-list-card' : ''}`}>
                        <div className="loja-grid-icon">{link.icon}</div>
                        <span className="loja-grid-label">{link.label}</span>
                      </a>
                    )
                  ))}
                </div>
              </div>
            )}

            {customLinks.length > 0 && (
              <div className="loja-section">
                <h5 className="loja-section-title">Links</h5>
                <div className={viewMode === 'list' ? 'loja-links-list' : 'loja-links-grid'}>
                  {customLinks.map((link, i) => (
                    <a
                      key={i}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`loja-grid-card${link.destaque ? ' loja-grid-card--destaque' : ''}${viewMode === 'list' ? ' loja-list-card' : ''}`}
                    >
                      <div className="loja-grid-icon">{link.icon}</div>
                      <span className="loja-grid-label">{link.label}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

           
          </div>
        </div>

        {/* ===== MOBILE LAYOUT ===== */}
        <div className="loja-mobile">
          <div className="container py-3">
            <div className="row justify-content-center">
              <div className="col-12">
                <div className="mb-3" style={{ position: 'relative', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
                  <div style={{ position: 'relative', height: '120px', overflow: 'hidden' }}>
                    {/* Imagem de fundo - com grayscale se fechada */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundImage: backgroundImage || 'none',
                      backgroundColor: backgroundColor || '#000',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center center',
                      backgroundSize: 'cover',
                      filter: isFechamentoTemporario ? 'grayscale(1)' : 'none'
                    }} />
                  </div>
                  <div className="p-3 text-center" style={{ marginTop: '-48px', position: 'relative', zIndex: 2 }}>
                    {loja.logo_url && (
                      <img
                        src={`https://grubupload.s3.amazonaws.com/${loja.logo_url}`}
                        alt={loja.nome}
                        className="mb-2"
                        style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', border: '3px solid #fff', position: 'relative', zIndex: 3 }}
                      />
                    )}
                    <h2 style={{ fontWeight: 700, color: '#1a1a1a', fontSize: '1.5rem', marginBottom: '4px' }}>{loja.nome}</h2>
                    {loja.tipo && (
                      <div className="mb-2">
                        <span className="badge text-uppercase text-black" style={{ backgroundColor: '#f4f4f5', letterSpacing: '0.03em', fontSize: '0.7rem', fontWeight: 700 }}>{loja.tipo}</span>
                      </div>
                    )}
                    {loja.endereco && (
                      <p className="mb-2" style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                        {loja.endereco.logradouro} {loja.endereco.numero}
                        {loja.endereco.complemento && ` ${loja.endereco.complemento}`}
                        {' '}{loja.endereco.bairro}
                        {loja.endereco.cidade && ` - ${loja.endereco.cidade}`}
                      </p>
                    )}
                    {loja.horarios && loja.horarios.length > 0 && (
                      <div className="text-center">
                        <div className="mb-2">
                          {isFechamentoTemporario ? (
                            <span className="badge bg-warning text-dark rounded-pill status-badge">{lojaStatus.badge}</span>
                          ) : isOpen ? (
                            <span className="badge bg-success rounded-pill status-badge">{lojaStatus.badge}</span>
                          ) : (
                            <span className="badge bg-danger rounded-pill status-badge">{lojaStatus.badge}</span>
                          )}
                        </div>
                        {isFechamentoTemporario && (fechamentoData?.motivo || fechamentoData?.reabertura_programada) && (
                          <div className="mt-2 mb-2" style={{ background: '#fef3c7', borderRadius: '8px', padding: '8px 14px', display: 'inline-block' }}>
                            {fechamentoData?.motivo && (
                              <span className="d-block" style={{ fontSize: '0.85rem', color: '#92400e' }}>
                                {fechamentoData.motivo}
                              </span>
                            )}
                            {fechamentoData?.reabertura_programada && (
                              <span className="d-block" style={{ fontSize: '0.8rem', color: '#92400e', opacity: 0.85, marginTop: fechamentoData?.motivo ? '4px' : '0' }}>
                                🕐 Reabertura prevista: {formatarReabertura(fechamentoData.reabertura_programada)}
                              </span>
                            )}
                          </div>
                        )}
                        {!isOpen && !isFechamentoTemporario && lojaStatus.proximaAbertura && (
                          <div className="mt-2 mb-2" style={{ background: '#fef2f2', borderRadius: '8px', padding: '8px 14px', display: 'block' }}>
                            <span className="d-block" style={{ fontSize: '0.8rem', color: '#991b1b', opacity: 0.85 }}>
                              Próxima abertura: {lojaStatus.proximaAbertura}
                            </span>
                          </div>
                        )}
                        <button
                          className="btn btn-link text-decoration-none p-0 d-inline-flex align-items-center"
                          style={{ fontSize: '0.85rem', color: '#6b7280', gap: '4px' }}
                          onClick={() => setShowHorarios(!showHorarios)}
                        >
                          Horários de funcionamento
                          <NavArrowDown
                            width={16}
                            height={16}
                            style={{ transform: showHorarios ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
                          />
                        </button>
                        {showHorarios && (
                          <div className="mt-3 pt-3" style={{ borderTop: '1px solid #e5e7eb' }}>
                            <div className="d-flex flex-column" style={{ gap: '6px' }}>
                              {horariosAgrupados.map((horario, index) => {
                                return (
                                  <div key={index} className="d-flex justify-content-between align-items-center" style={{ fontSize: '0.9rem' }}>
                                    <span style={{ color: '#4b5563', fontWeight: 500 }}>{NOMES_DIAS[horario.dia]}</span>
                                    <span style={{ color: horario.ativo ? '#1a1a1a' : '#9ca3af', fontWeight: 600 }}>
                                      {horario.ativo ? formatarTurnosDia(horario.turnos) : 'Fechado'}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Toggle Grid/Lista */}
                <div className="loja-mobile-view-header">
                  <div className="loja-view-toggle">
                    <button 
                      className={`loja-view-btn${viewMode === 'grid' ? ' active' : ''}`}
                      onClick={() => toggleViewMode('grid')}
                      title="Visualizar em grade"
                    >
                      <GridPlus width={18} height={18} />
                    </button>
                    <button 
                      className={`loja-view-btn${viewMode === 'list' ? ' active' : ''}`}
                      onClick={() => toggleViewMode('list')}
                      title="Visualizar em lista"
                    >
                      <List width={18} height={18} />
                    </button>
                  </div>
                </div>

                <div className={viewMode === 'list' ? 'loja-mobile-list' : 'loja-mobile-grid'}>
                  {sortedMainLinks.map((link, i) => (
                    isLinkDisabled(link) ? (
                      <div key={i} className={`loja-grid-card loja-grid-card--disabled${viewMode === 'list' ? ' loja-list-card' : ''}`} title={lojaStatus.mensagem || 'Loja fechada'}>
                        <div className="loja-grid-icon">{link.icon}</div>
                        <span className="loja-grid-label">{link.label}</span>
                      </div>
                    ) : (
                      <Link key={i} to={link.to} className={`loja-grid-card${link.badge ? ' loja-grid-card--has-badge' : ''}${viewMode === 'list' ? ' loja-list-card' : ''}`}>
                        {link.badge && (
                          <div className="loja-ribbon">
                            {link.badge}
                            <div className="loja-ribbon-shine" />
                          </div>
                        )}
                        <div className="loja-grid-icon">{link.icon}</div>
                        <span className="loja-grid-label">{link.label}</span>
                      </Link>
                    )
                  ))}
                  {socialLinks.map((link, i) => (
                    link.onClick ? (
                      <button key={`ext-${i}`} className={`loja-grid-card${viewMode === 'list' ? ' loja-list-card' : ''}`} onClick={link.onClick}>
                        <div className="loja-grid-icon">{link.icon}</div>
                        <span className="loja-grid-label">{link.label}</span>
                      </button>
                    ) : (
                      <a key={`ext-${i}`} href={link.href} target="_blank" rel="noopener noreferrer" className={`loja-grid-card${viewMode === 'list' ? ' loja-list-card' : ''}`}>
                        <div className="loja-grid-icon">{link.icon}</div>
                        <span className="loja-grid-label">{link.label}</span>
                      </a>
                    )
                  ))}
                  {customLinks.map((link, i) => (
                    <a
                      key={`custom-${i}`}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`loja-grid-card${link.destaque ? ' loja-grid-card--destaque' : ''}${viewMode === 'list' ? ' loja-list-card' : ''}`}
                    >
                      <div className="loja-grid-icon">{link.icon}</div>
                      <span className="loja-grid-label">{link.label}</span>
                    </a>
                  ))}
                </div>

                <div className="mt-4 text-center">
                  <a href="https://grub.digital" target="_blank" rel="noopener noreferrer">
                    <img src="https://grubicons.s3.us-east-1.amazonaws.com/powered-by-grub.png" alt="Powered by Grub" style={{ maxWidth: '160px', width: '100%' }} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer
          loja={loja}
          onMeusPedidosClick={() => setShowMeusPedidos(true)}
          onLoginClick={() => setShowLogin(true)}
          onCriarContaClick={() => setShowLogin(true)}
        />

        <LoginModal
          show={showLogin}
          onHide={() => setShowLogin(false)}
          onLogin={handleLogin}
          brandLogoUrl={loja?.logo_url}
        />

        {showMeusPedidos && (
          <MeusPedidosOffcanvas
            show={showMeusPedidos}
            onHide={() => setShowMeusPedidos(false)}
          />
        )}

      </div>
    </>
  );
};

export default Loja;
